import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { AlertTriangle, TrendingDown, Package, BarChart3, Star } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { API, formatCurrency } from "../../lib/helpers";

const ICON_MAP = {
  price_decline: TrendingDown,
  inventory_surge: Package,
  soft_market: BarChart3,
};

const COLOR_MAP = {
  price_decline: "text-red-500 bg-red-500/10 border-red-500/20",
  inventory_surge: "text-amber-500 bg-amber-500/10 border-amber-500/20",
  soft_market: "text-blue-500 bg-blue-500/10 border-blue-500/20",
};

export const MarketAlertsWidget = () => {
  const [alerts, setAlerts] = useState([]);

  useEffect(() => {
    axios.get(`${API}/market-alerts?limit=5`).then(r => setAlerts(r.data.alerts || [])).catch(() => {});
  }, []);

  if (alerts.length === 0) return null;

  return (
    <Card className="border-border" data-testid="market-alerts-widget">
      <CardHeader className="pb-2 pt-4 px-4">
        <div className="flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-amber-500" />
          <CardTitle className="text-sm font-semibold">Market Alerts</CardTitle>
        </div>
      </CardHeader>
      <CardContent className="px-4 pb-4 space-y-2">
        {alerts.map((a, i) => {
          const Icon = ICON_MAP[a.type] || AlertTriangle;
          const color = COLOR_MAP[a.type] || "text-muted-foreground bg-secondary/40 border-border";
          return (
            <div key={i} className={`flex items-start gap-2.5 p-2.5 rounded-lg border ${color}`} data-testid={`market-alert-${i}`}>
              <Icon className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-xs font-medium">{a.city}, {a.state}</p>
                <p className="text-[11px] text-muted-foreground">{a.message}</p>
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
};


export const DealOfDayWidget = () => {
  const [deal, setDeal] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/deal-of-the-day`).then(r => {
      if (r.data && r.data.property) setDeal(r.data);
    }).catch(() => {});
  }, []);

  if (!deal) return null;

  const prop = deal.property || {};
  const score = deal.deal_score || 0;

  const handleClick = () => {
    navigate(`/dashboard?property=${prop.id}`);
  };

  return (
    <Card className="border-border overflow-hidden cursor-pointer card-hover transition-shadow hover:shadow-md" onClick={handleClick} data-testid="deal-of-day-widget">
      <CardHeader className="pb-2 pt-4 px-4">
        <div className="flex items-center gap-2">
          <Star className="w-4 h-4 text-amber-500" />
          <CardTitle className="text-sm font-semibold">Deal of the Day</CardTitle>
          <Badge className={`text-[10px] border ml-auto ${score >= 60 ? "text-emerald-500 bg-emerald-500/10 border-emerald-500/30" : "text-amber-500 bg-amber-500/10 border-amber-500/30"}`}>
            Score: {score}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="px-4 pb-4">
        <div className="flex gap-3">
          {prop.image_url && (
            <img src={prop.image_url} alt={prop.address} className="w-20 h-20 rounded-lg object-cover flex-shrink-0" loading="lazy" />
          )}
          <div className="min-w-0">
            <p className="text-sm font-semibold truncate">{prop.address}</p>
            <p className="text-xs text-muted-foreground">{prop.city}, {prop.state} {prop.zip_code}</p>
            <div className="flex gap-3 mt-1.5">
              <span className="text-xs font-mono font-bold">{formatCurrency(prop.price)}</span>
              {deal.cash_flow_monthly != null && (
                <span className={`text-xs font-mono font-bold ${deal.cash_flow_monthly >= 0 ? "text-emerald-500" : "text-red-500"}`}>
                  {formatCurrency(deal.cash_flow_monthly)}/mo
                </span>
              )}
              {deal.cap_rate != null && (
                <span className="text-xs font-mono font-bold text-blue-500">{deal.cap_rate.toFixed(1)}% cap</span>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
